"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const async_storage_1 = __importDefault(require("@react-native-async-storage/async-storage"));
const axios_1 = __importDefault(require("axios"));
const buffer_1 = require("buffer");
const enc_base64_1 = __importDefault(require("crypto-js/enc-base64"));
const hmac_sha256_1 = __importDefault(require("crypto-js/hmac-sha256"));
const helpers_1 = require("./helpers");
function delayResolve(cb, ms = 250) {
    return new Promise((resolve) => setTimeout(() => resolve(cb()), ms));
}
var StorageKeys;
(function (StorageKeys) {
    StorageKeys["KEY"] = "sessionToken";
    StorageKeys["TICKETS"] = "tokens";
})(StorageKeys || (StorageKeys = {}));
var AuthEvents;
(function (AuthEvents) {
    AuthEvents["AUTHENTICATED"] = "AUTHENTICATED";
    AuthEvents["UNAUTHENTICATED"] = "UNAUTHENTICATED";
})(AuthEvents || (AuthEvents = {}));
var ClientStates;
(function (ClientStates) {
    ClientStates[ClientStates["BOOTSTRAPPING"] = 0] = "BOOTSTRAPPING";
    ClientStates[ClientStates["IDLE"] = 1] = "IDLE";
    ClientStates[ClientStates["FETCHING"] = 2] = "FETCHING";
})(ClientStates || (ClientStates = {}));
class AuthJSONClient {
    constructor({ url } = {}) {
        this._state = ClientStates.BOOTSTRAPPING;
        this._key = null;
        this._tickets = [];
        this._jwtToken = null;
        this._lastTicket = '';
        this._subscribers = {};
        this.url = '';
        this.bootstrap();
        if (url)
            this.url = url;
    }
    static get TICKETS_LENGTH_THRESHOLD() {
        return 12;
    }
    static get Events() {
        return AuthEvents;
    }
    get lastToken() {
        return this._lastTicket;
    }
    async _popTicket() {
        if (this._state !== ClientStates.IDLE)
            return await delayResolve(this._popTicket);
        if (this._tickets.length < AuthJSONClient.TICKETS_LENGTH_THRESHOLD)
            await this._fetchMoreTickets();
        const ticket = this._tickets.pop();
        await async_storage_1.default.setItem(StorageKeys.TICKETS, JSON.stringify(this._tickets));
        this._lastTicket = ticket || '';
        console.log('tickets left in storage: ', this._tickets.length);
        return ticket;
    }
    async _fetchMoreTickets() {
        try {
            this._state = ClientStates.FETCHING;
            console.log(`fetching more tickets, currently ${this._tickets.length} tickets left in storage`);
            const ticket = this._tickets.pop();
            await async_storage_1.default.setItem(StorageKeys.TICKETS, JSON.stringify(this._tickets));
            if (!ticket)
                return this._publish(AuthEvents.UNAUTHENTICATED);
            const requestUrl = helpers_1.urlBuilder({
                baseAddress: this.url,
                callType: 'GetTickets',
                authJson: await this.getAuthJSON(ticket),
            });
            const { data } = await axios_1.default.get(requestUrl, { timeout: 4000 });
            // @todo handle refetching tickets and failed authentication
            if (data === helpers_1.AUTH_FAILED_ERROR)
                return this._fetchMoreTickets();
            // @todo handle unknown response from server
            if (!Array.isArray(data))
                return;
            console.log(`fetched ${data.length} new tickets`);
            await this.addTickets(data);
        }
        catch (e) {
            console.log('fetch more tickets error: ', e.message);
        }
        finally {
            this._state = ClientStates.IDLE;
        }
    }
    _publish(event) {
        Object.values(this._subscribers)
            .filter((subscriber) => subscriber.event === event)
            .forEach(({ callback }) => callback());
    }
    async bootstrap() {
        this._key = await async_storage_1.default.getItem(StorageKeys.KEY);
        this._tickets = JSON.parse((await async_storage_1.default.getItem(StorageKeys.TICKETS)) || '[]');
        this._state = ClientStates.IDLE;
        return { isSignout: typeof this._key !== 'string' || this._tickets.length === 0, permissions: [] };
    }
    async clear() {
        await async_storage_1.default.multiRemove([StorageKeys.KEY, StorageKeys.TICKETS]);
        this._state = ClientStates.IDLE;
        this._key = null;
        this._tickets = [];
        this._jwtToken = null;
        this._lastTicket = '';
    }
    on(event, callback) {
        const subscriberId = Date.now().toString();
        this._subscribers[subscriberId] = { event, callback };
        return () => {
            delete this._subscribers[subscriberId];
        };
    }
    async setKey(key) {
        this._key = key;
        await async_storage_1.default.setItem(StorageKeys.KEY, key);
    }
    async addTickets(tickets) {
        this._tickets.unshift(...tickets);
        await async_storage_1.default.setItem(StorageKeys.TICKETS, JSON.stringify(this._tickets));
    }
    async getAuthJSON(ticket) {
        if (!ticket)
            ticket = await this._popTicket();
        if (!ticket || !this._key) {
            this._publish(AuthEvents.UNAUTHENTICATED);
            return;
        }
        const eTicket = encodeURIComponent(hmac_sha256_1.default(ticket, this._key).toString(enc_base64_1.default));
        return JSON.stringify({ eTicket });
    }
    async getJwtToken() {
        try {
            if (this._jwtToken) {
                const { exp } = JSON.parse(buffer_1.Buffer.from(this._jwtToken.split('.')[1], 'base64').toString());
                if (exp * 1000 > Date.now())
                    return this._jwtToken;
            }
            const authJson = await this.getAuthJSON();
            if (!authJson)
                return;
            console.log(`fetching new jwt token`);
            const url = helpers_1.urlBuilder({
                baseAddress: 'https://rahtab.net/DL/RahtabLogin/SecurityService.aspx',
                callType: 'GetJwtToken',
                authJson,
            });
            const { data: token } = await axios_1.default.get(url, {
                timeout: 4000,
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0',
                },
            });
            if (!token)
                return;
            this._jwtToken = token;
            return token;
        }
        catch (e) {
            console.log('get jwt token error: ', e.message);
        }
    }
}
exports.default = AuthJSONClient;
